import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { FC, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useIndexQuery } from "../../api/genericHooks";
import { useUserLanguage } from "../../hooks/useUserLanguage";
import i18n from "../../i18n";
import { selectUser } from "../../store/slices/auth";
import { dispatch } from "../../store/store";
import { setUser } from "../../store/slices/auth";
import { Language } from "../../types/models/Language";

export const LanguageSelectorModalButton: FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const user = useSelector(selectUser);
  const { updateUserLanguage } = useUserLanguage(user?.id);

  const languages = useIndexQuery("/languages", null, {
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  }).data;

  const savedLanguage = localStorage.getItem("i18nextLng");
  const supportedLngs =
    (i18n?.options?.supportedLngs &&
      i18n?.options?.supportedLngs.filter((lng) => lng !== "cimode")) ||
    [];

  const languagesList =
    languages?.filter((language) => supportedLngs.includes(language.local)) ||
    [];

  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );
  const initializedRef = useRef(false);

  const onLanguageChange = async (language: Language) => {
    setSelectedLanguage(language);
    localStorage.setItem("i18nextLng", language.local);
    localStorage.setItem("i18nextReadingDirection", language.reading_direction);
    i18n.changeLanguage(language.local);

    // Mettre à jour la langue dans le backend si l'utilisateur est connecté
    if (user) {
      try {
        await updateUserLanguage(language);
        // Mettre à jour l'utilisateur dans le store Redux
        dispatch(setUser({ user: { ...user, language } }));
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la langue:", error);
      }
    }
  };

  // Mettre à jour selectedLanguage quand languagesList change (seulement au premier chargement)
  useLayoutEffect(() => {
    if (languagesList.length > 0 && !initializedRef.current) {
      initializedRef.current = true;

      // Si l'utilisateur est connecté et a une langue sauvegardée, l'utiliser
      if (user?.language) {
        const userLanguage = languagesList.find(
          (lang) => lang.id === user.language?.id
        );
        if (userLanguage) {
          setSelectedLanguage(userLanguage);
          localStorage.setItem("i18nextLng", userLanguage.local);
          localStorage.setItem(
            "i18nextReadingDirection",
            userLanguage.reading_direction
          );
          i18n.changeLanguage(userLanguage.local);
          return;
        }
      }

      // Sinon, utiliser la langue sauvegardée dans le localStorage ou la première langue
      const defaultLanguage =
        languagesList.find((language) => language.local === savedLanguage) ||
        languagesList[0];

      setSelectedLanguage(defaultLanguage);
    }
  }, [languagesList, savedLanguage, user?.language?.id]);

  const buttonContent = selectedLanguage
    ? `${selectedLanguage.emoji_flag} ${selectedLanguage.name}`
    : t("loading"); // ou un autre texte/placeholder adapté

  return (
    <>
      <Dialog open={open} onClose={setOpen} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800"
                  >
                    {t("languageModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />
                  <div className="w-full mt-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
                      {languagesList.map((lang) => (
                        <button
                          key={lang.id}
                          onClick={() => onLanguageChange(lang)}
                          className={[
                            selectedLanguage === lang
                              ? "bg-white border-2 border-green-500 text-green-700"
                              : "bg-white border text-slate-700",
                            "p-2 flex text-xs sm:text-sm font-medium rounded items-center justify-center transition-colors duration-200",
                          ].join(" ")}
                        >
                          {`${lang.emoji_flag} ${lang.name}`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="mt-3 inline-flex w-full justify-center rounded bg-rust-50 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:mt-0 sm:w-auto"
                >
                  {t("languageModal.save")}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <button
        onClick={() => setOpen(true)}
        className="bg-white border text-slate-700 p-1.5 sm:p-2 px-2 sm:px-4 flex text-xs sm:text-sm font-medium rounded items-center justify-center transition-colors duration-200"
      >
        {buttonContent}
      </button>
    </>
  );
};
